import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, Check, AlertTriangle } from 'lucide-react';
import { API } from '../../../utils/api';

export const MAX_REFERENCES = 3;

const MODES = [
  { value: 'next_phase', label: 'Próxima fase', hint: 'continua a periodização com progressão' },
  { value: 'same_style', label: 'Mesmo estilo', hint: 'treino novo no mesmo padrão e fase' },
];

const ReferenceItem = ({ item, selected, disabled, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={disabled}
    className={`flex items-start gap-3 text-left p-3 rounded-xl border transition-colors duration-200 disabled:opacity-40 ${
      selected ? 'border-neon-lime/60 bg-neon-lime/5' : 'border-white/10 bg-zinc-950 hover:border-white/20'
    }`}
    data-testid={`ai-reference-${item.id}`}
  >
    <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected ? 'bg-neon-lime border-neon-lime' : 'border-zinc-600'}`}>
      {selected && <Check className="w-3 h-3 text-black" />}
    </span>
    <span className="min-w-0">
      <span className="block text-sm text-white font-semibold truncate">{item.name}</span>
      {item.description && <span className="block text-xs text-zinc-500 line-clamp-1">{item.description}</span>}
      <span className={`flex items-center gap-1 text-[11px] mt-1 ${item.exercises_count ? 'text-zinc-400' : 'text-amber-400'}`}>
        {!item.exercises_count && <AlertTriangle className="w-3 h-3" />}
        {item.days_count} dia(s) · {item.exercises_count ? `${item.exercises_count} exercícios` : 'sem treinos salvos vinculados'}
      </span>
    </span>
  </button>
);

export const AiReferencePicker = ({ selectedIds, onChange, mode, onModeChange }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/ai-workouts/references`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const toggle = (id) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  if (items.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="ai-reference-picker">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
          <BookOpen className="w-3.5 h-3.5" /> Usar como base (opcional, até {MAX_REFERENCES})
        </span>
        <span className="text-xs text-zinc-500" data-testid="ai-reference-count">{selectedIds.length}/{MAX_REFERENCES}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
        {items.map((item) => {
          const selected = selectedIds.includes(item.id);
          return (
            <ReferenceItem
              key={item.id}
              item={item}
              selected={selected}
              disabled={!selected && selectedIds.length >= MAX_REFERENCES}
              onToggle={() => toggle(item.id)}
            />
          );
        })}
      </div>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="ai-reference-mode">
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={mode === m.value}
              onClick={() => onModeChange(m.value)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${
                mode === m.value ? 'bg-neon-lime text-black border-neon-lime font-bold' : 'border-white/10 text-zinc-300 hover:border-white/30'
              }`}
              data-testid={`ai-mode-${m.value}`}
            >
              {m.label} <span className={`text-xs ${mode === m.value ? 'text-black/70' : 'text-zinc-500'}`}>· {m.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
